import {
  Box,
  Group,
  Stack,
  Text,
  Tooltip,
  useMantineTheme,
} from '@mantine/core'

import { Ban } from 'lucide-react'

interface SignalIconProps {
  signalLevel: number | null
}

// The bands this icon renders, in descending strength. Exported so the Signal column's filter
// offers exactly what the icon can display, and typed as the label below so adding a band to
// one without the other is a compile error rather than a silent mismatch.
export const SIGNAL_BAND_VALUES = [
  'Excellent',
  'Good',
  'Weak',
  'Very Weak',
  'No Signal',
] as const

export type SignalBand = (typeof SIGNAL_BAND_VALUES)[number]

// An absent reading has no band: the icon shows N/A rather than a strength, so callers treat
// the empty string as "not filterable", the same way an underivable power status is treated.
export const getSignalBand = (
  signalLevel: number | null | undefined,
): SignalBand | '' =>
  signalLevel === null || signalLevel === undefined
    ? ''
    : getSignalStrength(signalLevel).label

const getSignalStrength = (
  signal: number,
): {
  bars: number
  color: string
  label: SignalBand
} => {
  if (signal >= 21) {
    return {
      bars: 4,
      color: 'green',
      label: 'Excellent',
    }
  }

  if (signal >= 15) {
    return {
      bars: 3,
      color: 'lime',
      label: 'Good',
    }
  }

  if (signal >= 10) {
    return {
      bars: 2,
      color: 'yellow',
      label: 'Weak',
    }
  }

  if (signal > 0) {
    return {
      bars: 1,
      color: 'red',
      label: 'Very Weak',
    }
  }

  return {
    bars: 0,
    color: 'gray',
    label: 'No Signal',
  }
}

export const SignalIcon = ({ signalLevel }: SignalIconProps) => {
  const theme = useMantineTheme()

  if (
    signalLevel === null ||
    signalLevel === undefined
  ) {
    return (
      <Box>
        <Tooltip
          label="Signal unavailable"
        >
          <Group gap={6}>
            <Ban
              size={18}
              color={
                theme.colors.gray[6]
              }
            />

            <Text
              size="sm"
              c="dimmed"
            >
              N/A
            </Text>
          </Group>
        </Tooltip>
      </Box>
    )
  }

  const signal =
    getSignalStrength(signalLevel)

  return (
    <Box display="inline-flex">
      <Tooltip
        multiline
        withArrow
        label={
          <Stack gap={2}>
            <Text size="sm">CSQ: {signalLevel}</Text>
            <Text size="xs">{signal.label}</Text>
          </Stack>
        }
      >
        <Group gap={8}>
          <Group gap={3} align="flex-end" wrap="nowrap">
            {[1, 2, 3, 4].map((bar) => {
              const active = bar <= signal.bars
              return (
                <Box
                  key={bar}
                  w={5}
                  h={6 + bar * 4}
                  bg={active ? signal.color : 'gray.3'}
                  style={{
                    borderRadius: 2,
                    transition: 'all 150ms ease',
                    opacity: active ? 1 : 0.4,
                  }}
                />
              )
            })}
          </Group>
          <Text size="sm" fw={500} c={signal.color}>
            {signalLevel}
          </Text>
        </Group>
      </Tooltip>
    </Box>
  )
}